import { DEVICE, MARKET_AUDIENCE } from "../../utils/constants";
import { graphql, useStaticQuery } from "gatsby";

import { Box } from "@vinomofo/components";
import { HeaderNarrow } from "./HeaderNarrow";
import { HeaderWide } from "./HeaderWide";
import { Media } from "gatsby-plugin-fresnel";
import { MofoPromises } from "../mofo-promises/MofoPromises";
import { hasSpiritsInPath } from "../../utils/is-page";
import { storefrontNavigationItemProps } from "../../types/vino-data-types";
import styled from "styled-components";
import { useIsWinePage } from "../../hooks/use-is-wine-page";

interface NavigationLink {
  name: string;
  nameMobile: string;
  link: {
    url: string;
  };
  device: Array<"desktop" | "mobile">;
}
const HeaderBackgroundImage = styled(Box)<{ $backgroundImage: string }>`
  background: linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
    url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media ${({ theme }) => theme.mq.max.mobile} {
    @media (min-aspect-ratio: 1/1) {
      height: 100vw;
    }

    @media (orientation: landscape) {
      height: 100vw;
    }
  }
`;

const WHISKYMOFO_ENABLED = process.env.GATSBY_ENABLE_WHISKYMOFO === "true";

export const Header = ({
  pathname,
  bgImage = "",
}: {
  pathname?: string;
  bgImage?: string;
}): JSX.Element => {
  const isWinePage = useIsWinePage();
  const isSpiritPage = hasSpiritsInPath(pathname);
  const pageHasBG = bgImage !== "";
  const isWhiskymofoEnabled = WHISKYMOFO_ENABLED && isSpiritPage;

  const { vinodata } = useStaticQuery(graphql`
    query {
      vinodata {
        whiskymofoMenu: headerNavigation(name: "Whiskymofo") {
          name
          list {
            name
            nameMobile
            link {
              url
            }
            device
          }
        }
        defaultMenu: headerNavigation {
          name
          list {
            name
            nameMobile
            link {
              url
            }
            device
          }
        }
      }
    }
  `);

  const headerData = isWhiskymofoEnabled
    ? vinodata.whiskymofoMenu
    : vinodata.defaultMenu;

  let menuItems: storefrontNavigationItemProps[] =
    headerData?.list?.map((item: NavigationLink) => ({
      link: item.link?.url,
      label: item.name,
      labelMobile: item.nameMobile,
      audience: MARKET_AUDIENCE.ALL,
      device: item.device,
    })) || [];

  if (isWhiskymofoEnabled) {
    menuItems = [];
  }

  return (
    <Box position={"relative"}>
      {bgImage && (
        <HeaderBackgroundImage
          $backgroundImage={bgImage}
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={-1}
          height={"100vh"}
          width={"100%"}
          opacity={1}
          display={"block"}
          maxWidth={"1920px"}
          m={"0 auto"}
        />
      )}
      <Media between={["sm", "md"]}>
        <HeaderNarrow
          menuItems={menuItems}
          isWhiskymofoEnabled={isWhiskymofoEnabled}
          transparentHeader={pageHasBG}
        />
      </Media>
      <Media between={["md", "lg"]}>
        <HeaderWide
          menuItems={menuItems?.map((item) => ({
            ...item,
            label: item.labelMobile || item.label,
          }))}
          isWhiskymofoEnabled={isWhiskymofoEnabled}
          transparentHeader={pageHasBG}
        />
      </Media>
      <Media greaterThanOrEqual="lg">
        <HeaderWide
          menuItems={menuItems}
          isWhiskymofoEnabled={isWhiskymofoEnabled}
          transparentHeader={pageHasBG}
        />
      </Media>
      {isWinePage && <MofoPromises />}
    </Box>
  );
};

Header.displayName = "Header";
